/**
 * Attribute each applied promotion to the order lines it discounted.
 *
 * `affectedItems.itemUuid` is the menu item, not the order line. The promotion
 * SDK writes one entry per participating line (`qty` and `originalPrice`), so
 * lines are recovered by matching those values.
 */

const DISCARDED_ITEM_STATUSES = new Set([
  "VOID",
  "RETURN",
  "CANCELED",
  "CANCELLED",
  "REJECTED",
  "REJECTED_BY_ORDERS_CO",
]);

const COMPED_ITEM_STATUSES = new Set(["COMP_AFTER_TXN", "COMP_BEFORE_TXN"]);

const PRICE_TOLERANCE = 0.01;

const roundMoney = (value) => Math.round(value * 100) / 100;

const cleanLabel = (value) => String(value ?? "").trim();

const namedLabel = (...values) => {
  for (const value of values) {
    const text = cleanLabel(value);
    if (text) {
      return text;
    }
  }
  return "";
};

/** "Coupon (name)" / "Promotion (name)". The kind alone when there is no name. */
export function formatDiscountKindLabel(kind, name) {
  const label = cleanLabel(name);
  return label ? `${kind} (${label})` : kind;
}

export function promotionAdjustmentLabel(adjustment) {
  const code = cleanLabel(adjustment?.promotionCode);
  const candidates = [adjustment?.promotionName, adjustment?.displayLabel]
    .map(cleanLabel)
    .filter(Boolean);
  const name =
    candidates.find((value) => value.toLowerCase() !== code.toLowerCase()) ||
    candidates[0] ||
    "";
  if (code) {
    return formatDiscountKindLabel("Coupon", name || code);
  }
  return formatDiscountKindLabel("Promotion", name);
}

export function couponDetailLabel(detail) {
  return formatDiscountKindLabel(
    "Coupon",
    namedLabel(
      detail?.couponTitle,
      detail?.couponName,
      detail?.promotionName,
      detail?.discountCode,
      detail?.couponCode
    )
  );
}

const isCompedItem = (item) => {
  const statusName = item?.status?.name;
  return !!statusName && COMPED_ITEM_STATUSES.has(statusName);
};

const isPromotionAttributableItem = (item) => {
  const statusName = item?.status?.name;
  return !statusName || !DISCARDED_ITEM_STATUSES.has(statusName);
};

const matchesQty = (affected, line) => {
  const lineQty = line.count || 0;
  return (affected.qty ?? lineQty) === lineQty;
};

const matchesQtyAndPrice = (affected, line) => {
  if (!matchesQty(affected, line)) {
    return false;
  }
  const linePrice = roundMoney(line.price || 0);
  const affectedPrice = roundMoney(affected.originalPrice ?? linePrice);
  return Math.abs(linePrice - affectedPrice) <= PRICE_TOLERANCE;
};

const addDiscount = (perLine, lineUuid, amount) => {
  perLine.set(lineUuid, (perLine.get(lineUuid) ?? 0) + Math.abs(amount));
};

const prorateByCount = (perLine, lines, amount) => {
  const totalCount = lines.reduce((sum, line) => sum + (line.count || 1), 0);
  if (totalCount <= 0) {
    return;
  }

  let remaining = roundMoney(amount);
  lines.forEach((line, index) => {
    const isLast = index === lines.length - 1;
    const share = isLast
      ? remaining
      : roundMoney(((line.count || 1) / totalCount) * amount);
    remaining = roundMoney(remaining - share);
    if (share > 0 && line.uuid) {
      addDiscount(perLine, line.uuid, share);
    }
  });
};

const attributePromotionToLines = (promotion, linesByMenuItem) => {
  const perLine = new Map();
  const affectedByMenuItem = new Map();

  for (const affected of promotion.affectedItems ?? []) {
    const menuItemUuid = affected?.itemUuid;
    if (!menuItemUuid || !linesByMenuItem.has(menuItemUuid)) {
      continue;
    }
    const group = affectedByMenuItem.get(menuItemUuid);
    if (group) {
      group.push(affected);
    } else {
      affectedByMenuItem.set(menuItemUuid, [affected]);
    }
  }

  for (const [menuItemUuid, affectedItems] of affectedByMenuItem) {
    const lines = linesByMenuItem.get(menuItemUuid) ?? [];
    const claimed = new Set();
    let pending = affectedItems;

    for (const matches of [matchesQtyAndPrice, matchesQty]) {
      if (!pending.length) {
        break;
      }
      const stillPending = [];
      for (const affected of pending) {
        const line = lines.find(
          (candidate) =>
            !!candidate.uuid &&
            !claimed.has(candidate.uuid) &&
            matches(affected, candidate)
        );
        if (!line?.uuid) {
          stillPending.push(affected);
          continue;
        }
        claimed.add(line.uuid);
        addDiscount(perLine, line.uuid, affected.discountAmount || 0);
      }
      pending = stillPending;
    }

    if (pending.length) {
      const leftover = pending.reduce(
        (sum, affected) => sum + Math.abs(affected.discountAmount || 0),
        0
      );
      const unclaimed = lines.filter(
        (line) => !!line.uuid && !claimed.has(line.uuid)
      );
      prorateByCount(perLine, unclaimed.length ? unclaimed : lines, leftover);
    }
  }

  for (const [lineUuid, amount] of perLine) {
    perLine.set(lineUuid, roundMoney(amount));
  }

  return perLine;
};

const groupAttributableLinesByMenuItem = (items) => {
  const linesByMenuItem = new Map();
  const compedLines = [];

  for (const item of items) {
    if (!item?.uuid || !item.itemUuid || !isPromotionAttributableItem(item)) {
      continue;
    }
    if (isCompedItem(item)) {
      compedLines.push(item);
      continue;
    }
    const lines = linesByMenuItem.get(item.itemUuid);
    if (lines) {
      lines.push(item);
    } else {
      linesByMenuItem.set(item.itemUuid, [item]);
    }
  }

  for (const item of compedLines) {
    const lines = linesByMenuItem.get(item.itemUuid);
    if (lines) {
      lines.push(item);
    } else {
      linesByMenuItem.set(item.itemUuid, [item]);
    }
  }

  return linesByMenuItem;
};

export function collectOrderItems(categories) {
  if (!Array.isArray(categories)) return [];
  return categories.flatMap((category) =>
    Array.isArray(category?.items) ? category.items : []
  );
}

export function buildPromotionAdjustmentsByLineUuid(items, promotions) {
  const adjustmentsByLineUuid = new Map();
  if (!items?.length || !promotions?.length) {
    return adjustmentsByLineUuid;
  }

  const linesByMenuItem = groupAttributableLinesByMenuItem(items);
  if (!linesByMenuItem.size) {
    return adjustmentsByLineUuid;
  }

  for (const promotion of promotions) {
    const perLine = attributePromotionToLines(promotion, linesByMenuItem);
    for (const [lineUuid, discountAmount] of perLine) {
      if (discountAmount <= 0) {
        continue;
      }
      const adjustments = adjustmentsByLineUuid.get(lineUuid) ?? [];
      adjustments.push({
        promotionName: promotion.promotionName,
        promotionCode: promotion.promotionCode || null,
        displayLabel:
          promotion.displayLabel || promotion.promotionName || "Promotion",
        discountAmount,
      });
      adjustmentsByLineUuid.set(lineUuid, adjustments);
    }
  }

  return adjustmentsByLineUuid;
}
